/**
 * Multi-Timestep Optimization with Battery Storage
 * 
 * This module handles the dispatch of generators and batteries across multiple time periods
 * using a single integrated optimization problem.
 */

import { GridState } from '../types';
import { BezierCurve, evaluateBezierCurve } from '../bezier/bezier';
import { logger } from '../utils/logger';
import { solve, HighsRawSolution, Model } from './highs';
import { buildData } from './language/buildData';
import { DERIVED_RULES, VARIABLE_RULES, constraintRulesFor, COST_TERMS, GRID_CATALOG } from './language/constraintRules';
import { RuleProgram, groundProgram } from './language/ruleSet';

/**
 * Extended GridState with solution status
 */
export interface GridStateWithSolution extends GridState {
  solutionValid?: boolean;
  solutionMessage?: string;
  models?: Map<number, Model>; // componentId -> Model object
  lpSolutions?: Map<number, HighsRawSolution>; // componentId -> raw HiGHS solution
}

/**
 * Tagged union for hourly data that can either contain generator values or be infeasible
 */
export type HourlyData = 
  | { type: 'feasible'; hour: number; generators: Array<{ id: string; value: number }> }
  | { type: 'infeasible'; hour: number };

/**
 * Result for each hour including battery state
 */
export interface HourlyDispatchResult {
  hour: number;
  gridState: GridStateWithSolution;
  batteryStates?: Map<string, { soc: number; power: number }>; // nodeId -> state of charge and power
}

/**
 * The rules the model is built from when the caller doesn't supply any — the
 * hand-authored set, with `requireMatchingSOC` picking the final-SOC variant.
 * The app passes its own (editable) program instead; this keeps every other
 * caller, and the tests, on the authored defaults.
 */
export function defaultRuleProgram(requireMatchingSOC: boolean = false): RuleProgram {
  return {
    derivedRules: DERIVED_RULES,
    variableRules: VARIABLE_RULES,
    constraintRules: constraintRulesFor(requireMatchingSOC),
    costTerms: COST_TERMS,
    catalog: GRID_CATALOG,
  };
}

/**
 * Build (but do not solve) the multi-timestep LP for a grid.
 *
 * Factored out of {@link solveMultiTimestepDispatch} so callers that need the
 * model itself — e.g. the ranging-based LMP-curve sweep, which loads it into a
 * persistent HiGHS instance and mutates one constraint's RHS — can build it the
 * same way the solver does.
 *
 * `program` is the compiled rule set to ground; when omitted the authored
 * defaults are used (and `requireMatchingSOC` selects the final-SOC variant —
 * a program of its own already carries that choice in its `final_soc` rule).
 */
export function buildMultiTimestepModel(
  gridTopology: GridState,
  loadCurvePoints: BezierCurve,
  windCurvePoints?: BezierCurve,
  solarCurvePoints?: BezierCurve,
  numTimeSteps: number = 24,
  requireMatchingSOC: boolean = false,
  overrideNodalDemand?: { hour: number; demands: Map<string, number> },
  hourOffset: number = 0,
  program?: RuleProgram
): Model {
  const { nodes, edges } = gridTopology;

  const data = buildData({
    nodes,
    edges,
    loadCurve: loadCurvePoints,
    windCurve: windCurvePoints,
    solarCurve: solarCurvePoints,
    numHours: numTimeSteps,
    hourOffset,
    overrideNodalDemand,
  });
  return groundProgram(program ?? defaultRuleProgram(requireMatchingSOC), data);
}

/**
 * Solve multi-timestep optimization with batteries
 *
 * This function solves a single optimization problem across all 24 hours,
 * allowing batteries to shift energy between time periods.
 */
export async function solveMultiTimestepDispatch(
  gridTopology: GridState,
  loadCurvePoints: BezierCurve,
  windCurvePoints?: BezierCurve,
  solarCurvePoints?: BezierCurve,
  numTimeSteps: number = 24,
  requireMatchingSOC: boolean = false,
  overrideNodalDemand?: { hour: number; demands: Map<string, number> },
  hourOffset: number = 0,
  program?: RuleProgram
): Promise<HourlyDispatchResult[]> {
  const { nodes, edges } = gridTopology;
  const numHours = numTimeSteps;
  
  // Separate nodes by type
  const generators = nodes.filter(n => n.type === 'generator');
  const batteries = nodes.filter(n => n.type === 'battery');
  const loads = nodes.filter(n => n.type === 'load');
  
  // For now, we'll solve as a single connected component
  // In a real implementation, we'd handle multiple components
  
  
  try {
    // Create the multi-timestep model
    const model = buildMultiTimestepModel(
      gridTopology,
      loadCurvePoints,
      windCurvePoints,
      solarCurvePoints,
      numHours,
      requireMatchingSOC,
      overrideNodalDemand,
      hourOffset,
      program
    );

    logger.debug('Multi-timestep Model created with', model.variables.length, 'variables and', model.constraints.length, 'constraints');
    
    // Solve the problem
    const solution = await solve(model);
    
    if (solution.status !== 'Optimal') {
      logger.warn(`Multi-timestep optimization failed: ${solution.status}`);
      // Return empty results for infeasible solution
      return Array.from({ length: numHours }, (_, hour) => ({
        hour,
        gridState: {
          ...gridTopology,
          solutionValid: false,
          solutionMessage: `Solver status: ${solution.status}`,
          models: new Map([[0, model]]),
          lpSolutions: solution.raw ? new Map([[0, solution.raw]]) : undefined
        },
        batteryStates: new Map()
      }));
    }
    
    // Extract results for each hour
    const hourlyResults: HourlyDispatchResult[] = [];
    
    for (let t = 0; t < numHours; t++) {
      // Create grid state for this hour
      const hourlyNodes = nodes.map(node => {
        const updatedNode = { ...node, value: 0 };

        // Set generator values
        if (node.type === 'generator') {
          const varName = `P_gen_${node.id}_t${t}`;
          updatedNode.value = solution.variables.get(varName) || 0;
        }

        // Set battery power (positive for discharge, negative for charge)
        if (node.type === 'battery') {
          const chargeVar = `P_charge_${node.id}_t${t}`;
          const dischargeVar = `P_discharge_${node.id}_t${t}`;
          const charge = solution.variables.get(chargeVar) || 0;
          const discharge = solution.variables.get(dischargeVar) || 0;
          updatedNode.value = discharge - charge; // Net power output
        }

        // Set load values (negative)
        if (node.type === 'load') {
          // Calculate the total load for this hour from the load curve
          const totalLoadMW = evaluateBezierCurve(loadCurvePoints, t + hourOffset);

          // Get this load's original demand
          const loadDemand = node.maxPowerMW ? Math.abs(node.maxPowerMW) :
                            node.value ? Math.abs(node.value) : 0;

          // Calculate total demand from all loads
          const totalDemand = loads.reduce((sum, l) => {
            const demand = l.maxPowerMW ? Math.abs(l.maxPowerMW) :
                          l.value ? Math.abs(l.value) : 0;
            return sum + demand;
          }, 0);

          // Use proportional distribution (preserves original load ratios)
          if (totalDemand > 0 && loadDemand > 0) {
            const loadProportion = loadDemand / totalDemand;
            updatedNode.value = -(totalLoadMW * loadProportion);
          } else if (loads.length > 0) {
            // Fallback to even distribution
            updatedNode.value = -(totalLoadMW / loads.length);
          } else {
            updatedNode.value = 0;
          }
        }

        // Extract voltage angles
        const slackBusId = nodes[0]?.id;
        if (node.id === slackBusId) {
          updatedNode.voltageAngle = 0;
        } else {
          const thetaVar = `theta_${node.id}_t${t}`;
          updatedNode.voltageAngle = solution.variables.get(thetaVar) || 0;
        }

        // Set voltage magnitude (always 1.0 in DC power flow)
        updatedNode.voltage = 1.0;

        // Extract LMP (Locational Marginal Price) from nodal balance constraint dual
        // The dual value on the power balance constraint represents the LMP
        const balanceConstraintName = `balance_${node.id}_t${t}`;
        const dualValue = solution.dualVariables?.get(balanceConstraintName);
        if (dualValue !== undefined) {
          // For a minimization problem with equality constraint (generation - load = 0),
          // the dual value directly represents the marginal cost (LMP)
          // Take absolute value to ensure positive prices
          updatedNode.lmp = Math.abs(dualValue);
        }

        return updatedNode;
      });
      
      // Extract line flows for this hour
      const hourlyEdges = edges.map(edge => {
        const flowVar = `P_line_${edge.id}_t${t}`;
        const flow = solution.variables.get(flowVar) || 0;
        
        // Calculate current from power flow. In this simplified DC model the
        // per-unit voltage is fixed at 1.0, so current magnitude equals the
        // power flow. The line rating (which bounds P_line in the LP) is in the
        // same units, so utilization = current / rating reads correctly.
        //
        // Sign convention: in the nodal balance, P_line has coefficient +1 at
        // the edge's `from` node and -1 at its `to` node, which makes the
        // physical from->to flow equal to -P_line. The UI treats positive
        // current as flow in the from->to direction, so negate to match.
        const current = -flow;
        
        return { ...edge, current };
      });
      
      // Extract battery states for this hour
      const batteryStates = new Map<string, { soc: number; power: number }>();
      batteries.forEach(bat => {
        const socVar = `SOC_${bat.id}_t${t}`;
        const soc = solution.variables.get(socVar) || 0;
        
        const chargeVar = `P_charge_${bat.id}_t${t}`;
        const dischargeVar = `P_discharge_${bat.id}_t${t}`;
        const charge = solution.variables.get(chargeVar) || 0;
        const discharge = solution.variables.get(dischargeVar) || 0;
        const power = discharge - charge;
        
        batteryStates.set(bat.id, { soc, power });
      });
      
      hourlyResults.push({
        hour: t,
        gridState: {
          nodes: hourlyNodes,
          edges: hourlyEdges,
          solutionValid: true,
          solutionMessage: 'Optimal solution found',
          models: new Map([[0, model]]),
          lpSolutions: solution.raw ? new Map([[0, solution.raw]]) : undefined
        },
        batteryStates
      });
    }
    
    return hourlyResults;
    
  } catch (error) {
    logger.error('Multi-timestep optimization error:', error);
    
    // Return error results
    return Array.from({ length: numHours }, (_, hour) => ({
      hour,
      gridState: {
        ...gridTopology,
        solutionValid: false,
        solutionMessage: `Error: ${error instanceof Error ? error.message : String(error)}`,
        models: undefined,
        lpSolutions: undefined
      },
      batteryStates: new Map()
    }));
  }
}

/**
 * Get hourly grid states using multi-timestep optimization
 * 
 * This is the main entry point for computing hourly grid states.
 * It always uses the multi-timestep model which handles batteries optimally
 * and also works correctly when no batteries are present.
 */
export async function getHourlyGridStates(
  gridTopology: GridState,
  loadCurvePoints: BezierCurve,
  windCurvePoints?: BezierCurve,
  solarCurvePoints?: BezierCurve,
  numTimeSteps: number = 24,
  requireMatchingSOC: boolean = false,
  overrideNodalDemand?: { hour: number; demands: Map<string, number> },
  hourOffset: number = 0,
  program?: RuleProgram
): Promise<Array<{ hour: number; gridState: GridStateWithSolution; batteryStates?: Map<string, { soc: number; power: number }> }>> {
  // Always use multi-timestep optimization
  const results = await solveMultiTimestepDispatch(
    gridTopology,
    loadCurvePoints,
    windCurvePoints,
    solarCurvePoints,
    numTimeSteps,
    requireMatchingSOC,
    overrideNodalDemand,
    hourOffset,
    program
  );

  return results;
}